"use client";

import { useQuery } from "@tanstack/react-query";
import { useGraphql } from "~/api-client";
import type { User } from "~/types";

interface HookResult {
  user: User | null;
  refetch: () => Promise<User | null>;
  error: unknown;
  isValidating: boolean;
  isLoading: boolean;
  isGuest: boolean;
}

export function useUser(): HookResult {
  const { getCurrentUser } = useGraphql();
  const { data, refetch, error, isFetching, isLoading } = useQuery({
    queryKey: ["user"],
    queryFn: getCurrentUser,
    // This doesn't change during a session, except after refetch is called
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const user: User | null = data?.me
    ? {
        isPaymentsVerified: data.me.isPaymentsVerified,
        firstName: data.me.firstName,
        lastName: data.me.lastName,
        dob: data.me.dob || null,
        emailAddress: data.me.emailAddress || null,
        phoneNumber: data.me.phoneNumber || null,
        hasVerifiedSession: Boolean(data.session?.verified),
        hasAgreedToTerms: data.me.hasAgreedToTerms,
      }
    : null;

  const refetchUser = async () => {
    const result = await refetch();
    const data = result.data;
    const user: User | null = data?.me
      ? {
          isPaymentsVerified: data.me.isPaymentsVerified,
          firstName: data.me.firstName,
          lastName: data.me.lastName,
          dob: data.me.dob || null,
          emailAddress: data.me.emailAddress || null,
          phoneNumber: data.me.phoneNumber || null,
          hasVerifiedSession: Boolean(data.session?.verified),
          hasAgreedToTerms: data.me.hasAgreedToTerms,
        }
      : null;
    return user;
  };

  const isGuest = Boolean(data?.session?.guest);

  return {
    user,
    isGuest,
    refetch: refetchUser,
    error,
    isValidating: isFetching,
    isLoading,
  };
}
